import { ChevronDown, ChevronUp } from '@myosh/icons';
import { Badge, Button, Checkbox, Combobox, Field, FieldLabel } from '@myosh/odin-components';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface FilterOption {
  id: number;
  label: string;
}

const CHIP_LIMIT = 10;

interface CheckboxListFilterProps {
  label: string;
  options: FilterOption[];
  selectedIds: number[];
  onChange: (selectedIds: number[]) => void;
  isLoading?: boolean;
}

// The parent is checked while any of its options is selected; checking it selects them all.
export const CheckboxListFilter = ({
  label,
  options,
  selectedIds,
  onChange,
  isLoading = false,
}: CheckboxListFilterProps) => {
  const { t } = useTranslation();
  const checkboxId = useId();
  const [isExpanded, setIsExpanded] = useState(false);

  const optionIds = new Set(options.map((option) => option.id));
  const selectedOptions = options.filter((option) => selectedIds.includes(option.id));
  const count = selectedOptions.length;

  const replaceSelection = (next: FilterOption[]) => {
    onChange([...selectedIds.filter((id) => !optionIds.has(id)), ...next.map((option) => option.id)]);
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <Field orientation="horizontal" className="w-auto">
          <Checkbox
            id={checkboxId}
            checked={count > 0}
            disabled={isLoading}
            onChange={(event) => replaceSelection(event.target.checked ? options : [])}
          />
          <FieldLabel htmlFor={checkboxId} className="font-normal">
            {label}
          </FieldLabel>
        </Field>
        {count > 0 && !isExpanded && (
          <Badge variant="secondary" aria-label={t(($) => $.selectedCount, { count })}>
            {count}
          </Badge>
        )}
        <Button
          variant="ghost"
          size="icon-xs"
          className="ml-auto"
          aria-label={`${isExpanded ? t(($) => $.collapse) : t(($) => $.expand)} ${label}`}
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? <ChevronUp /> : <ChevronDown />}
        </Button>
      </div>
      {isExpanded && (
        <div className="pl-6">
          <Combobox
            multiple
            items={options}
            value={selectedOptions}
            onValueChange={replaceSelection}
            getKey={(option) => String(option.id)}
            getLabel={(option) => option.label}
            placeholder={t(($) => $.select)}
            collapseMultiple={count > CHIP_LIMIT}
            isLoading={isLoading}
          />
        </div>
      )}
    </div>
  );
};
